import React from 'react';
import { Controller } from 'react-hook-form';
import { IconAlertTriangle, IconArrowRight } from '@tabler/icons-react';
import { Alert } from '@planne-software/uni';
import { Button } from '@planne-software/uni/Button';
import { Input } from '@planne-software/uni/Input';
import { useTranslate } from '@src/core/hooks';
import {
  AuthCardButtonWrapper,
  AuthCardRoot,
  AuthCardSubtitle,
  AuthCardTextGroup,
  AuthCardTitle,
} from '@src/features/customers';

import { useEmailIdentificationForm } from '../hooks/useEmailIdentificationForm';
import type { EmailIdentificationPayload } from '../types';

type EmailIdentificationCardProps = {
  onIdentify: (payload: EmailIdentificationPayload) => void;
  onEmailSubmit?: (email: string) => void;
};

export const EmailIdentificationCard = (props: EmailIdentificationCardProps) => {
  const { onIdentify, onEmailSubmit } = props;
  const { i18n } = useTranslate();
  const { control, errors, hasGenericError, loading, retryGenericError, submitSignupIntent } =
    useEmailIdentificationForm(onIdentify, { onEmailSubmit });

  return (
    <AuthCardRoot data-testid='email-identification-card'>
      <AuthCardTextGroup>
        <AuthCardTitle>{i18n('checkout_auth_identification_title')}</AuthCardTitle>
        <AuthCardSubtitle>{i18n('checkout_auth_identification_subtitle')}</AuthCardSubtitle>
      </AuthCardTextGroup>

      <form onSubmit={submitSignupIntent} noValidate>
        <AuthCardRoot>
          <Controller
            name='email'
            control={control}
            render={({ field: { onChange, value = '', name, ref, ...rest } }) => (
              <Input
                type='email'
                label={i18n('checkout_auth_email_label')}
                name={name}
                id={name}
                value={value}
                onChange={onChange}
                error={!!errors.email}
                disabled={loading}
                helperText={errors.email?.message}
                autoComplete='email'
                ref={ref}
                size='medium'
                {...rest}
              />
            )}
          />

          {hasGenericError ? (
            <Alert
              severity='warning'
              canClose={false}
              title={i18n('checkout_auth_generic_error_title')}
              subTitle={i18n('checkout_auth_generic_error')}
              startIcon={<IconAlertTriangle size={20} aria-hidden />}
              action={
                <Button type='button' variant='slim' size='sm' onClick={retryGenericError}>
                  {i18n('checkout_auth_generic_error_retry')}
                </Button>
              }
              sx={{ marginTop: 0, width: '100%' }}
            />
          ) : null}

          <AuthCardButtonWrapper>
            <Button type='submit' fullWidth loading={loading} endSlot={<IconArrowRight aria-hidden />}>
              {i18n('checkout_auth_identification_cta')}
            </Button>
          </AuthCardButtonWrapper>
        </AuthCardRoot>
      </form>
    </AuthCardRoot>
  );
};

export default EmailIdentificationCard;
